import { CreditResolutionMethod, isEnum } from 'corso-types';
import { isMatching, match, P } from 'ts-pattern';
import { z } from 'zod';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { isAppEmbedded } from '~/utils/appEmbed';
import { computeCost, sumBy } from '~/utils/compute';
import { RollupStatusCode } from '~/utils/constants';
import { createCurrencyFormatter, formatDate } from '~/utils/formatters';
import { makeLoader, redirect } from '~/utils/routing';
import { getOriginatingOrderCreditAlert } from '~/utils/theme';
import { AsyncReturnType } from '~/utils/types';

type Claim = AsyncReturnType<typeof api.fetchCrewClaim>;
type Shipment = NonNullable<Claim['claimReturnShipments']>[number];
type ClaimLineItem = Claim['claimLineItems'][number];

const formatResolutionSummary = (claim: Claim) => {
  // TODO revisit types, as these may no longer actually be optional
  const {
    refunds = [],
    giftCards = [],
    exchangeOrders = [],
  } = claim.resolutionSummary ?? {};

  return (
    claim.resolutionSummary && {
      ...(exchangeOrders.length && { exchangeOrders }),

      ...(refunds.length && {
        refundsTotal: {
          amount: sumBy(({ amount }: { amount: number }) => amount)(refunds),
          handlingFee: sumBy(
            ({ handlingFee }: { handlingFee: number }) => handlingFee,
          )(refunds),
          shippingFee: sumBy(
            ({ shippingFee }: { shippingFee: number }) => shippingFee,
          )(refunds),
        },
      }),

      ...(giftCards.length && {
        giftCardsTotal: {
          amount: sumBy(({ amount }: { amount: number }) => amount)(giftCards),
          incentiveAmount: sumBy(
            ({ incentiveAmount }: { incentiveAmount: number }) =>
              incentiveAmount,
          )(giftCards),
        },
      }),
    }
  );
};

const getReturnByDate = ({
  shipment,
  isInstantExchange,
  customerPayment,
}: {
  shipment: Shipment;
  isInstantExchange: boolean;
  customerPayment: Claim['customerPayment'];
}) => {
  const paymentExpiresOn =
    customerPayment?.authorizationExpiresOn ?? customerPayment?.capturedOn;

  return isInstantExchange && paymentExpiresOn
    ? paymentExpiresOn
    : shipment.shipmentExpiresOn;
};

const getReturnByText = (
  returnByDate: string,
  isExpired: boolean,
  isCancelled: boolean,
  claimType: Claim['claimType'],
  isConsolidated: boolean,
) => {
  const claimTypeText = claimType === 'Return' ? 'Return' : 'Warranty';
  if (isCancelled) return `${claimTypeText} Canceled`;

  if (isExpired) {
    return `${claimTypeText} Expired On: ${formatDate(returnByDate)}`;
  }

  if (isConsolidated) {
    return `Drop off your items by ${formatDate(returnByDate)} at any Happy Returns® location`;
  }

  return `Return Items By: ${formatDate(returnByDate)}`;
};

const getReturnShipmentTitle = ({
  shipmentInTransit,
  status,
  returnByDate,
  isReturnByDateExpired,
  isCancelled,
  claimType,
  isConsolidated,
}: {
  shipmentInTransit: boolean;
  status: Claim['claimRollup']['code'];
  returnByDate: string;
  isReturnByDateExpired: boolean;
  isCancelled: boolean;
  claimType: Claim['claimType'];
  isConsolidated: boolean;
}) => {
  if (shipmentInTransit) {
    return 'Items you returned';
  }

  if (returnByDate) {
    return getReturnByText(
      returnByDate,
      isReturnByDateExpired,
      isCancelled,
      claimType,
      isConsolidated,
    );
  }

  const statusHeadline = {
    [RollupStatusCode.inProgress]: 'Next Steps',
    [RollupStatusCode.readyForReview]: 'We are reviewing your information',
    [RollupStatusCode.completed]: 'We have reviewed your request',
  } satisfies Record<RollupStatusCode, string>;

  return statusHeadline[status];
};

const getReturnShipmentTypeText = (shipment: Shipment) =>
  match(shipment)
    .with(
      { returnShipmentType: 'Label', qrCodeDetail: P.nonNullable },
      () => 'QR Code',
    )
    .with({ returnShipmentType: 'Label' }, () => 'label')
    .with({ returnShipmentType: 'Packing_Slip' }, () => 'packing slip')
    .with({ returnShipmentType: 'Consolidated_Dropoff' }, () => 'QR Code')
    .exhaustive();

const getShipmentNotInTransitText = (
  shipment: Shipment,
  isInstantExchange: boolean,
) =>
  `Please use the provided ${getReturnShipmentTypeText(
    shipment,
  )} to return your items${
    isInstantExchange
      ? '. Because you selected an instant exchange, your new order will be processed immediately.'
      : ''
  }`;

const getReturnShipmentSubtitle = ({
  claim: {
    isInstantExchange,
    claimRollup: { code: status },
  },
  shipment,
  isExpired,
  isCancelled,
}: {
  claim: Claim;
  shipment: Shipment;
  isExpired: boolean;
  isCancelled: boolean;
}) => {
  if (isCancelled) return null;

  if (isExpired && status !== 'Completed') {
    return 'Your return has expired';
  }

  const inTransitOn =
    shipment.returnShipmentType === 'Label' ? shipment.inTransitOn : undefined;

  if (!inTransitOn) {
    return getShipmentNotInTransitText(shipment, isInstantExchange);
  }

  const statusTitles = {
    [RollupStatusCode.inProgress]: `Your return is in progress`,
    [RollupStatusCode.readyForReview]: 'Your return is in-review',
    [RollupStatusCode.completed]: 'Your return is complete',
  } satisfies Record<RollupStatusCode, string>;

  return statusTitles[status];
};

const addCostToLineItem = (lineItem: ClaimLineItem) => ({
  ...lineItem,
  // the claim line's own value: on an exchange of an exchange, what was paid on the originating order
  cost: computeCost({
    unitPrice: lineItem.unitPrice,
    quantity: lineItem.quantity,
  }),
});

const downloadLabels = {
  Label: 'Download Return Label',
  Packing_Slip: 'Download Packing Slip',
  // the QR code is rendered inline, there's nothing to download
  Consolidated_Dropoff: undefined,
} satisfies Record<Shipment['returnShipmentType'], string | undefined>;

const formatShipment = (claim: Claim) => (shipment: Shipment) => {
  // we only show "return shipments" here
  // shipments where the customer is returning the items
  if (!shipment.isReturnShipment) return null;

  const returnByDate = getReturnByDate({
    shipment,
    isInstantExchange: claim.isInstantExchange,
    customerPayment: claim.customerPayment,
  });
  const isExpired =
    !!returnByDate && Date.now() >= new Date(returnByDate).getTime();

  const isCancelled = claim.claimLineItems.some((li) => li.canceledOn);

  const isConsolidated = shipment.returnShipmentType === 'Consolidated_Dropoff';

  return {
    ...shipment,
    returnByDate,
    isExpired,
    title: getReturnShipmentTitle({
      shipmentInTransit:
        shipment.returnShipmentType === 'Label' && !!shipment.inTransitOn,
      status: claim.claimRollup.code,
      returnByDate,
      isReturnByDateExpired: isExpired,
      isCancelled,
      claimType: claim.claimType,
      isConsolidated,
    }),
    subtitle: getReturnShipmentSubtitle({
      claim,
      shipment,
      isExpired,
      isCancelled,
    }),
    claimLineItems: shipment.claimLineItems.map(addCostToLineItem),
    downloadLabel: downloadLabels[shipment.returnShipmentType],
  };
};

type Settings = AsyncReturnType<typeof api.fetchSettings>;

const formatRequestSummary = (claim: Claim, settings: Settings) => {
  // ? we don't want to show the request summary for a warranty claim, to prevent confusion about what a customer *may* get
  if (claim.claimType === 'Warranty') return undefined;
  const replacementOrderLineItems =
    claim.claimLineItems
      .find((li) => li.resolutionMethodEnum === 'Replacement_Order')
      ?.replacementItems?.map((ri) => ri)
      .filter(Boolean) ?? [];

  const variantExchangeLineItems = claim.claimLineItems
    .filter((li) => li.resolutionMethodEnum === 'Variant_Exchange')
    .map((lineItem) => lineItem.variantExchangeLineItem)
    .filter(Boolean)
    .concat(replacementOrderLineItems);

  const refundTotal = claim.requestSummary?.totals.refund ?? 0;
  const giftCardTotal = claim.requestSummary?.totals.giftCard ?? 0;

  // an untraced credit is still at $0; one a Shop Now basket absorbed is zeroed through deduction instead
  const untracedCreditMethods = new Set(
    (claim.requestSummary?.approvedLineItems ?? [])
      .filter((li) => li.amount === 0 && !li.wasZeroedThroughDeduction)
      .map((li) => li.resolutionMethodEnum)
      .filter((method) => isEnum(method, CreditResolutionMethod)),
  );

  const originatingOrderCreditAlert =
    settings.return.isClaimLineageEnabled &&
    claim.originalStoreOrder.isExchangeOfExchange &&
    untracedCreditMethods.size > 0
      ? getOriginatingOrderCreditAlert(settings.theme)
      : null;

  const showRefund =
    refundTotal > 0 ||
    (!!originatingOrderCreditAlert &&
      untracedCreditMethods.has(CreditResolutionMethod.refund));
  const showGiftCard =
    giftCardTotal > 0 ||
    (!!originatingOrderCreditAlert &&
      untracedCreditMethods.has(CreditResolutionMethod.giftCard));

  if (!variantExchangeLineItems.length && !showRefund && !showGiftCard)
    return undefined;

  const currencyFormatter = createCurrencyFormatter(
    claim.originalStoreOrder.currencyCode,
  );
  const isPositive = isMatching(P.number.positive());

  const showGiftCardChips = isPositive(giftCardTotal);
  const showRefundChips = isPositive(refundTotal) && !showGiftCardChips;

  const chips = [];

  if (isPositive(claim.giftCardIncentiveAmountApplied) && showGiftCardChips) {
    chips.push(
      `Incentive: +${currencyFormatter(claim.giftCardIncentiveAmountApplied)}`,
    );
  }

  if (isPositive(claim.feeApplied)) {
    chips.push(
      `${claim.feeName ?? 'Handling Fee'}: -${currencyFormatter(claim.feeApplied)}`,
    );
  }

  if (isPositive(claim.shopNowIncentiveAmountApplied)) {
    chips.push(
      `Shop Now Incentive: +${currencyFormatter(claim.shopNowIncentiveAmountApplied)}`,
    );
  }

  if (isPositive(claim.returnShippingAmountApplied)) {
    chips.push(
      `Return Shipping: -${currencyFormatter(claim.returnShippingAmountApplied)}`,
    );
  }

  if (isPositive(claim.exchangeOrderShippingAmountApplied)) {
    chips.push(
      `Exchange Shipping: -${currencyFormatter(claim.exchangeOrderShippingAmountApplied)}`,
    );
  }

  return {
    giftCardChips: showGiftCardChips ? chips : [],
    refundChips: showRefundChips ? chips : [],
    variantExchangeLineItems,
    refundTotal,
    giftCardTotal,
    showRefund,
    showGiftCard,
    originatingOrderCreditAlert,
  };
};

const getGreenReturns = (claim: Claim, theme: Settings['theme']) => {
  const returnItemIds = new Set(
    claim.claimReturnShipments.flatMap((shipment) =>
      shipment.claimLineItems.map((lineItem) => lineItem.id),
    ),
  );

  return {
    subtitle:
      claim.claimType === 'Return'
        ? (theme.noReturnRequiredItemsDetailText ??
          'You do not need to return these items.')
        : (theme.warrantyItemsDetailText ??
          'We will be in touch soon regarding your warranty claim.'),
    title:
      claim.claimType === 'Return'
        ? (theme.noReturnRequiredItemsHeadline ??
          'Items not required to return')
        : 'Items submitted',
    items: claim.claimLineItems
      .filter((lineItem) => !returnItemIds.has(lineItem.id))
      .map(addCostToLineItem),
  };
};

const paramsSchema = z.object({
  claimExternalId: z.string(),
  store: z.string().default(''),
});

export const { loader, useLoaderData: useClaimOverviewData } = makeLoader(
  ({ params, context }) => {
    const settings = context.get(settingsContext);
    const { claimExternalId, store } = paramsSchema.parse(params);

    return api
      .fetchCrewClaim({
        params: {
          storefrontId: settings.storefrontId,
          claimExternalId,
        },
      })
      .then((claim) => ({
        storeUrl: settings.storeUrl,
        currencyCode: claim.originalStoreOrder.currencyCode,
        claimExternalId: claim.externalId,
        claimRollup: claim.claimRollup,
        noteToCustomer: claim.noteToCustomer,
        greenReturns: getGreenReturns(claim, settings.theme),
        resolutionSummary: formatResolutionSummary(claim),
        requestSummary: formatRequestSummary(claim, settings),
        hideLabelDownloadWhenQrCodeIsPresent:
          settings.theme.hideLabelDownloadWhenQrCodeIsPresent ?? false,
        isCompleted: claim.claimRollup.code === RollupStatusCode.completed,
        isEmbedded: isAppEmbedded(),
        isCancelled: claim.claimLineItems.some((li) => li.canceledOn),
        returnShipments: claim.claimReturnShipments
          .map(formatShipment(claim))
          .filter(Boolean)
          .sort((a, b) => (a.createdOn > b.createdOn ? -1 : 1)),
      }))
      .catch(() => redirect(`/${store}`));
  },
);
